import { Controller } from "@hotwired/stimulus"
import html2canvas from "html2canvas";

export default class extends Controller {
  static targets = [
    "device",
    "wall",
    "image",
    "popupViewport",
    "devicePopup",
    "wallPopup",
    "devicePopupContent",
    "wallPopupContent",
    "deviceEditLink",
    "deviceDeleteLink",
    "wallEditLink",
    "wallDeleteLink",
    "planCanvas"
  ]
  static values = {
    walls: Array || [],
    projectId: String,
    floorPlanId: String
  }

  connect() {
    this.enabled = true
    this.isShow = false
    this.activeElement = null
    this.activeType = null // 'device' or 'wall'
    this.activeWall = null
    this.draggedPopup = null
    this.boundDragPopup = this.dragPopup.bind(this)
    this.boundStopPopupDrag = this.stopPopupDrag.bind(this)
    
    // Initialize wall Canvas
    this.initWallCanvas()
  }

  disconnect() {
    this.stopPopupDrag()
  }

  // Initialize wall Canvas
  initWallCanvas() {
    if (!this.hasImageTarget || !this.hasWallTarget) return
    
    this.isShow = false
    this.wall = this.wallTarget
    this.image = this.imageTarget
    this.ctx = this.wall.getContext("2d")
    
    // Wait for image to load before drawing walls
    if (this.image.complete) {
      this.drawWalls()
      // Initialize device positions after walls are drawn
      this.initDevicePositions()
      this.saveDeviceImage()
    } else {
      this.image.addEventListener('load', () => {
        this.drawWalls()
        // Initialize device positions after walls are drawn
        this.initDevicePositions()
        this.saveDeviceImage()
      })
    }
  }

  // Initialize device positions
  initDevicePositions() {
    if (!this.hasImageTarget) return
    
    const img = this.imageTarget
    
    // Ensure image is fully loaded
    if (!img.complete || img.naturalWidth === 0) {
      img.addEventListener('load', () => this.initDevicePositions())
      return
    }
    
    
    // Get image bounding rectangle for calculating correct positioning context
    const imgRect = img.getBoundingClientRect()
    const containerRect = this.element.querySelector('.floor-plan-img').getBoundingClientRect()
    
    
    // Calculate image offset relative to container
    const imgOffsetX = imgRect.left - containerRect.left
    const imgOffsetY = imgRect.top - containerRect.top
    
    
    // Adjust positions of all devices
    this.deviceTargets.forEach(deviceElement => {
      const deviceId = deviceElement.dataset.id
      const originalLeft = parseFloat(deviceElement.style.left.replace('px', ''))
      const originalTop = parseFloat(deviceElement.style.top.replace('px', ''))
      
      // Apply offset correction to ensure correct device positioning
      const correctedLeft = originalLeft
      const correctedTop = originalTop
      
      
      // Set corrected position
      deviceElement.style.left = `${correctedLeft}px`
      deviceElement.style.top = `${correctedTop}px`
    })
  }

  // Draw all walls
  drawWalls() {
    if (!this.ctx || !this.wallsValue || this.wallsValue.length === 0) return
    
    this.resizeCanvas()
    this.wallsValue.forEach(wall => {
      this.drawWall(wall.startX, wall.startY, wall.endX, wall.endY, wall.color)
    })
  }

  // Resize Canvas
  resizeCanvas() {
    if (!this.hasImageTarget || !this.hasWallTarget) return
    
    const canvas = this.wallTarget
    const img = this.imageTarget

    const width = img.clientWidth || img.naturalWidth
    const height = img.clientHeight || img.naturalHeight

    canvas.width = width
    canvas.height = height

    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
  }

  // Draw single wall
  drawWall(startX, startY, endX, endY, color) {
    this.ctx.beginPath()
    this.ctx.moveTo(startX, startY)
    this.ctx.lineTo(endX, endY)
    this.ctx.strokeStyle = color || "#000000"
    this.ctx.lineWidth = 5
    this.ctx.stroke()
  }

  // Handle device click
  onDeviceClick(event) {
    if (!this.enabled) return
    event.stopPropagation()

    const nextDevice = event.currentTarget
    const isSameDeviceSelected = this.activeType === "device" && this.activeElement === nextDevice && this.isShow
    if (isSameDeviceSelected) return

    this.resetActiveSelection()

    this.activeElement = nextDevice
    this.activeType = 'device'
    this.showDeviceInfo(this.activeElement.dataset)
    this.activeElement.classList.add("active")
  }

  // Handle wall click
  onWallClick(event) {
    if (!this.enabled) return
    event.stopPropagation()
    if (!this.hasWallPopupTarget) return

    const x = event.offsetX
    const y = event.offsetY
    const clickedWall = this.wallsValue.find(wall => this.isNearLine(x, y, wall))

    if (!clickedWall) {
      this.hidePopup()
      return
    }

    const isSameWallSelected = this.activeType === "wall" && this.activeWall?.id === clickedWall.id && this.isShow
    if (isSameWallSelected) return

    this.resetActiveSelection()

    this.activeType = 'wall'
    this.activeWall = clickedWall
    this.drawWall(clickedWall.startX, clickedWall.startY, clickedWall.endX, clickedWall.endY, "#3c9f00")
    this.showWallInfo(clickedWall)
  }

  onBackgroundClick(event) {
    if (!this.isShow) return
    if (event.target.closest(".canvas-popup")) return
    if (event.target.closest(".device__icon")) return
    if (event.target === this.wallTarget) return

    this.hidePopup()
  }

  // Show device information
  showDeviceInfo(device) {
    if (this.hasDevicePopupTarget && this.hasDevicePopupContentTarget) {
      const popup = this.devicePopupTarget
      const content = this.devicePopupContentTarget
      
      content.innerHTML = `
        <span id="popup-device-type" class="text-f1">
          Device Type: <span class="text-p5">${device.deviceType}</span>
        </span>
        <span id="popup-device-model" class="text-f1">
          Device Model: <span class="text-p5">${device.deviceModel}</span>
        </span>
        <span id="popup-placement" class="text-f1">
          Placement: <span class="text-p5">${device.placement}</span>
        </span>
      `

      // Set correct URL
      if (this.hasDeviceEditLinkTarget) {
        this.deviceEditLinkTarget.href = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/devices/${device.id}/edit`
      }
      if (this.hasDeviceDeleteLinkTarget) {
        // Set delete button URL
        const deleteLink = this.deviceDeleteLinkTarget
        deleteLink.dataset.url = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/plan_delete_device/${device.id}`
        
        // Set confirm button data-url for confirm_controller to use
        const confirmButton = document.getElementById('confirm_modal_delete_device')
        if (confirmButton) {
          confirmButton.dataset.url = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/plan_delete_device/${device.id}`
        }
      }

      popup.classList.remove("hidden")
      this.centerPopup(popup)
      this.isShow = true
    }
  }

  // Show wall information
  showWallInfo(wall) {
    if (this.hasWallPopupTarget && this.hasWallPopupContentTarget) {
      const popup = this.wallPopupTarget
      const content = this.wallPopupContentTarget
      
      content.innerHTML = `
        <span id="popup-material" class="text-f1">
          Material: <span class="text-p5">${wall.material}</span>
        </span>
        <span id="popup-thickness" class="text-f1">
          Thickness (${wall.unit}): <span class="text-p5">${wall.thickness}</span>
        </span>
      `

      // Set correct URL
      if (this.hasWallEditLinkTarget) {
        this.wallEditLinkTarget.href = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/walls/${wall.id}/edit`
      }
      if (this.hasWallDeleteLinkTarget) {
        // Set delete button URL
        const deleteLink = this.wallDeleteLinkTarget
        deleteLink.dataset.url = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/plan_delete_wall/${wall.id}`
        
        // Set confirm button data-url for confirm_controller to use
        const confirmButton = document.getElementById('confirm_modal_delete_wall')
        if (confirmButton) {
          confirmButton.dataset.url = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/plan_delete_wall/${wall.id}`
        }
      }

      popup.classList.remove("hidden")
      this.centerPopup(popup)
      this.isShow = true
    }
  }

  // Hide popup window
  hidePopup() {
    this.resetActiveSelection()
  }

  resetActiveSelection() {
    if (this.activeElement) {
      this.activeElement.classList.remove("active")
      this.activeElement = null
    }

    if (this.activeType === "wall" && this.activeWall) {
      this.drawWall(
        this.activeWall.startX,
        this.activeWall.startY,
        this.activeWall.endX,
        this.activeWall.endY,
        this.activeWall.color
      )
      this.activeWall = null
    }

    if (this.hasDevicePopupTarget) {
      this.devicePopupTarget.classList.add("hidden")
    }

    if (this.hasWallPopupTarget) {
      this.wallPopupTarget.classList.add("hidden")
    }

    this.isShow = false
    this.activeType = null
  }

  centerPopup(popup) {
    popup.style.transform = "translate(-50%, -50%)"
    popup.style.left = "50%"
    popup.style.top = "50%"
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  startPopupDrag(event) {
    if (event.button !== 0) return

    const popup = event.currentTarget.closest(".canvas-popup")
    if (!popup || popup.classList.contains("hidden") || !this.hasPopupViewportTarget) return

    event.preventDefault()
    event.stopPropagation()

    const viewportRect = this.popupViewportTarget.getBoundingClientRect()
    const popupRect = popup.getBoundingClientRect()
    const popupLeft = popupRect.left - viewportRect.left
    const popupTop = popupRect.top - viewportRect.top

    popup.style.transform = "none"
    popup.style.left = `${popupLeft}px`
    popup.style.top = `${popupTop}px`

    this.draggedPopup = popup
    this.popupDragOffset = {
      x: event.clientX - popupRect.left,
      y: event.clientY - popupRect.top
    }

    window.addEventListener("mousemove", this.boundDragPopup)
    window.addEventListener("mouseup", this.boundStopPopupDrag)
  }

  dragPopup(event) {
    if (!this.draggedPopup || !this.hasPopupViewportTarget) return

    const viewportRect = this.popupViewportTarget.getBoundingClientRect()
    const popupWidth = this.draggedPopup.offsetWidth
    const popupHeight = this.draggedPopup.offsetHeight

    const rawLeft = event.clientX - viewportRect.left - this.popupDragOffset.x
    const rawTop = event.clientY - viewportRect.top - this.popupDragOffset.y

    const maxLeft = Math.max(viewportRect.width - popupWidth, 0)
    const maxTop = Math.max(viewportRect.height - popupHeight, 0)

    const left = Math.min(Math.max(rawLeft, 0), maxLeft)
    const top = Math.min(Math.max(rawTop, 0), maxTop)

    this.draggedPopup.style.left = `${left}px`
    this.draggedPopup.style.top = `${top}px`
  }

  stopPopupDrag() {
    this.draggedPopup = null
    this.popupDragOffset = null
    window.removeEventListener("mousemove", this.boundDragPopup)
    window.removeEventListener("mouseup", this.boundStopPopupDrag)
  }

  // Check if point is near line segment
  isNearLine(x, y, wall) {
    const distance = this.pointToSegmentDistance(
      x, y,
      wall.startX, wall.startY,
      wall.endX, wall.endY
    )
    return distance <= 5
  }

  // Calculate distance from point to line segment
  pointToSegmentDistance(px, py, x1, y1, x2, y2) {
    const A = px - x1
    const B = py - y1
    const C = x2 - x1
    const D = y2 - y1

    const dot = A * C + B * D
    const lenSq = C * C + D * D
    let param = dot / lenSq

    if (param < 0) param = 0
    else if (param > 1) param = 1

    const xx = x1 + param * C
    const yy = y1 + param * D

    const dx = px - xx
    const dy = py - yy

    return Math.sqrt(dx * dx + dy * dy)
  }

  saveDeviceImage() {
    if (this.hasPlanCanvasTarget) {
      html2canvas(this.planCanvasTarget, {
        useCORS: true,
        allowTaint: false
      }).then(canvas => {
        canvas.toBlob(blob => {
          const formData = new FormData()
          formData.append("device", blob, `device.png`);

          if (!this.projectIdValue || !this.floorPlanIdValue) {
            throw new Error("Missing projectId or floorPlanId for device image upload")
          }

          const uploadPath = `/projects/${this.projectIdValue}/floor_plans/${this.floorPlanIdValue}/devices/upload_image`
          const tokenMeta = document.querySelector("meta[name=csrf-token]");
          const csrfToken = tokenMeta ? tokenMeta.content : "";
          fetch(uploadPath, {
            method: "POST",
            headers: {
              "X-CSRF-Token": csrfToken
            },
            body: formData
          }).then(resp => {
            if (!resp.ok) throw new Error("Upload failed")
          })
          .catch(console.error)
        })
      })
    }
  }

  toggle(event) {
    this.enabled = event.params.enabled
  }
}
